"use client";
import { Play, Headphones, Shield } from 'lucide-react';
import Link from 'next/link';

function YoutubeIcon({ size = 24, className = '' }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
    >
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
    </svg>
  );
}

export default function Home() {
  return (
    <main className="min-h-screen bg-white font-sans selection:bg-[#D6B570] selection:text-[#0B1528]">
      
      {/* 1. Header & Hero (Merged, Deep Navy background) */}
      <section className="bg-[#0B1528] pt-8 pb-32 px-6 relative overflow-hidden">
        {/* Subtelny glow w tle */}
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-[#D6B570]/5 rounded-full blur-[120px] pointer-events-none"></div>

        {/* Header */}
        <header className="max-w-7xl mx-auto flex justify-between items-center mb-24 relative z-10">
          <Link href="/" className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center border border-white/20 rounded-md">
              <Shield size={20} className="text-white" />
            </div>
            <div className="text-2xl font-bold tracking-widest uppercase text-white">
              Prawo<span className="text-white/60 font-light">Rodzinne</span>
            </div>
          </Link>
          <nav className="hidden md:flex gap-10 text-xs font-bold uppercase tracking-widest text-[#D6B570]">
            <Link href="#odcinki" className="hover:text-white transition-colors">Odcinki</Link>
            <Link href="#gospodarze" className="hover:text-white transition-colors">Eksperci</Link>
            <Link href="/eksperci/wioletta-czyz-zdunek" className="hover:text-white transition-colors">Kontakt</Link>
          </nav>
        </header>

        {/* Hero Content */}
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center relative z-10">
          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/10 text-xs font-bold text-white uppercase tracking-widest bg-white/5">
              <span className="w-2 h-2 rounded-full bg-[#D6B570]"></span>
              Nowy odcinek już dostępny
            </div>
            <h1 className="text-5xl lg:text-6xl xl:text-7xl font-extrabold leading-[1.1] text-white">
              Proste odpowiedzi na <span className="text-[#D6B570]">najtrudniejsze</span> pytania.
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed font-light max-w-lg">
              Pierwszy podcast o prawie rodzinnym tworzony przez Adwokata, Psychologa i Detektywa.
            </p>
            <div className="pt-4 flex flex-col sm:flex-row gap-4">
              <Link href="/podcast" className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#D6B570] text-[#0B1528] rounded-lg font-bold hover:bg-white transition-all shadow-[0_0_20px_rgba(214,181,112,0.3)] cursor-pointer">
                <Play fill="currentColor" size={20} /> Odtwórz Zwiastun
              </Link>
            </div>
          </div>
          
          <div className="relative aspect-square md:aspect-video lg:aspect-square bg-[#070D1A] rounded-3xl overflow-hidden border border-white/10 shadow-2xl group cursor-pointer">
            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1589829085413-56de8ae18c73?auto=format&fit=crop&q=80&w=1000')] bg-cover bg-center opacity-40 mix-blend-luminosity group-hover:scale-105 transition-transform duration-700"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B1528] via-transparent to-transparent opacity-80"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <Link href="/podcast" className="w-24 h-24 bg-white/10 backdrop-blur-md rounded-full border border-[#D6B570]/50 flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:bg-[#D6B570] transition-all">
                <Play size={40} className="text-[#D6B570] fill-[#D6B570] group-hover:text-[#0B1528] group-hover:fill-[#0B1528] ml-2 transition-colors" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Featured Listening Platforms (White bar) */}
      <section className="bg-white border-b border-gray-200 py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-center gap-6 md:gap-16">
          <span className="text-xs font-bold uppercase tracking-widest text-gray-400">Słuchaj nas na:</span>
          <div className="flex items-center gap-12 text-[#D6B570] font-bold text-xl">
            <span className="flex items-center gap-2 hover:text-[#0B1528] transition-colors cursor-pointer"><Headphones size={24}/> Spotify</span>
            <span className="flex items-center gap-2 hover:text-[#0B1528] transition-colors cursor-pointer"><Play size={24}/> Apple Podcasts</span>
            <span className="flex items-center gap-2 hover:text-[#0B1528] transition-colors cursor-pointer"><YoutubeIcon size={24}/> YouTube</span>
          </div>
        </div>
      </section>

      {/* 3. All Episodes (Pure White background) */}
      <section id="odcinki" className="bg-white py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-end mb-16">
            <h2 className="text-4xl md:text-5xl font-extrabold text-[#0B1528]">Najnowsze Odcinki</h2>
            <Link href="/podcast" className="hidden md:block text-[#D6B570] font-bold hover:text-[#0B1528] transition-colors border-b-2 border-[#D6B570] pb-1">
              Wszystkie odcinki &rarr;
            </Link>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { id: "01", title: "Zdrada i co dalej? Pierwsze kroki w sądzie.", date: "24 Wrz 2026" },
              { id: "02", title: "Jak zabezpieczyć dowody i własny majątek?", date: "15 Wrz 2026" },
              { id: "03", title: "Manipulacje i obrona dzieci przed narcyzem.", date: "01 Wrz 2026" }
            ].map((ep) => (
              <Link href="/podcast" key={ep.id} className="group bg-white border border-gray-200 rounded-2xl overflow-hidden hover:border-[#D6B570] hover:shadow-2xl transition-all duration-300 cursor-pointer flex flex-col block">
                <div className="p-8 flex-1 flex flex-col relative">
                  <div className="absolute top-0 right-0 p-6">
                     <button className="w-12 h-12 bg-gray-50 group-hover:bg-[#D6B570] rounded-full flex items-center justify-center transition-colors shadow-sm">
                       <Play size={20} className="text-[#D6B570] fill-[#D6B570] group-hover:text-white group-hover:fill-white ml-1" />
                     </button>
                  </div>
                  <div className="text-xs font-bold uppercase tracking-widest text-[#D6B570] mb-4">Odcinek #{ep.id}</div>
                  <h3 className="text-2xl font-bold mb-4 leading-snug text-[#0B1528] pr-12">
                    {ep.title}
                  </h3>
                  <div className="mt-auto pt-6 text-sm font-medium text-gray-400">
                    Opublikowano {ep.date}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 4. The Experts (Light Gray background) */}
      <section id="gospodarze" className="bg-gray-50 py-32 px-6 border-t border-gray-200">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-extrabold text-[#0B1528] text-center mb-6">Poznaj Gospodarzy</h2>
          <p className="text-center text-gray-500 mb-20 max-w-2xl mx-auto text-lg">
            Trzy różne perspektywy, jeden wspólny cel: przeprowadzić Cię bezpiecznie przez najtrudniejsze momenty życia.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            
            {/* Wioletta */}
            <Link href="/eksperci/wioletta-czyz-zdunek" className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100 hover:-translate-y-2 transition-transform duration-300 block">
              <div className="w-32 h-32 rounded-full overflow-hidden mb-8 border-4 border-gray-50 mx-auto shadow-inner bg-slate-100">
                <img src="/images/wioletta-czyz-zdunek.png" alt="Wioletta Czyż-Zdunek" className="w-full h-full object-cover object-top" />
              </div>
              <h3 className="text-2xl font-bold text-[#0B1528] text-center mb-2">Wioletta Czyż-Zdunek</h3>
              <div className="text-sm font-bold uppercase tracking-widest text-[#D6B570] text-center mb-6">Adwokat</div>
              <p className="text-gray-600 text-center leading-relaxed">
                Strategia prawna, ochrona majątku i reprezentacja w sądzie. Bezkompromisowa na sali rozpraw.
              </p>
            </Link>

            {/* Katarzyna */}
            <Link href="/eksperci/katarzyna-walczewska" className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100 hover:-translate-y-2 transition-transform duration-300 block">
              <div className="w-32 h-32 rounded-full overflow-hidden mb-8 border-4 border-gray-50 mx-auto shadow-inner bg-slate-100">
                <img src="/images/katarzyna-walczewska.png" alt="Katarzyna Walczewska" className="w-full h-full object-cover object-top" />
              </div>
              <h3 className="text-2xl font-bold text-[#0B1528] text-center mb-2">Katarzyna Walczewska</h3>
              <div className="text-sm font-bold uppercase tracking-widest text-[#D6B570] text-center mb-6">Psycholog</div>
              <p className="text-gray-600 text-center leading-relaxed">
                Wsparcie w kryzysie. Specjalizuje się w komunikacji z narcyzem i minimalizowaniu traumy u dzieci.
              </p>
            </Link>

            {/* Łukasz */}
            <Link href="/eksperci/lukasz-wiszniewski" className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100 hover:-translate-y-2 transition-transform duration-300 block">
              <div className="w-32 h-32 rounded-full overflow-hidden mb-8 border-4 border-gray-50 mx-auto shadow-inner bg-slate-100">
                <img src="/images/lukasz-wiszniewski.jpeg" alt="Łukasz Wiszniewski" className="w-full h-full object-cover object-top" />
              </div>
              <h3 className="text-2xl font-bold text-[#0B1528] text-center mb-2">Łukasz Wiszniewski</h3>
              <div className="text-sm font-bold uppercase tracking-widest text-[#D6B570] text-center mb-6">Detektyw</div>
              <p className="text-gray-600 text-center leading-relaxed">
                Ekspert od pozyskiwania twardych dowodów. Prowadzi analizy cyfrowe urządzeń mobilnych (ADB AppControl) i wykorzystuje psy pracujące do detekcji zapachowej.
              </p>
            </Link>

          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0B1528] py-12 text-center border-t border-white/10">
         <div className="text-gray-400 text-sm font-medium">© 2026 Prawo Rodzinne Podcast. Wszelkie prawa zastrzeżone.</div>
      </footer>
    </main>
  );
}
